import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { useLiveReload } from '../lib/live-context';
import { shortDate } from '../lib/money';
import { todos, type Todo, type TodoFilter } from '../lib/todos';

type FilterKey = 'open' | 'me' | 'them' | 'none' | 'done';

export function Todos() {
  const { state } = useAuth();
  const me = state.status === 'signedIn' ? state.me : null;
  const [items, setItems] = useState<Todo[] | null>(null);
  const [filter, setFilter] = useState<FilterKey>('open');
  const [error, setError] = useState<string | null>(null);

  const query: TodoFilter =
    filter === 'done' ? { status: 'done', assigned: 'all' }
    : filter === 'open' ? { status: 'open', assigned: 'all' }
    : { status: 'open', assigned: filter };

  const load = useCallback(async () => {
    const r = await todos.list(query);
    setItems(r.todos);
  }, [query.status, query.assigned]);

  useEffect(() => {
    load().catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load.'));
  }, [load]);

  useLiveReload(() => {
    void load().catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load.'));
  });

  if (!me) return null;

  return (
    <main className="page">
      <h1>Todos</h1>
      <p className="hint">
        Shared list.{' '}
        <Link to="/todos/recurring">Recurring</Link>
      </p>
      {error && <p className="error">{error}</p>}

      <QuickAdd
        myId={me.user.id}
        partner={me.partner}
        onSaved={load}
      />

      <div className="filters" role="tablist" aria-label="Filter todos">
        {(
          [
            ['open', 'Open'],
            ['me', 'Mine'],
            ['them', me.partner?.displayName ?? 'Theirs'],
            ['none', 'Anyone'],
            ['done', 'Done'],
          ] as const
        )
          .filter(([key]) => key !== 'them' || me.partner)
          .map(([key, label]) => (
            <button
              key={key}
              className={filter === key ? 'filter active' : 'filter'}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
      </div>

      {items === null ? (
        <p className="hint" aria-busy="true">
          Loading…
        </p>
      ) : items.length === 0 ? (
        <p className="hint">Nothing here.</p>
      ) : (
        <ul className="list todos">
          {items.map((t) => (
            <TodoRow
              key={t.id}
              todo={t}
              myId={me.user.id}
              partner={me.partner}
              onChanged={load}
            />
          ))}
        </ul>
      )}
    </main>
  );
}

function QuickAdd({
  myId,
  partner,
  onSaved,
}: {
  myId: number;
  partner: { id: number; displayName: string } | null;
  onSaved: () => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [assigned, setAssigned] = useState<string>('');
  const [due, setDue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await todos.add({
        title: title.trim(),
        assignedToUserId: assigned === '' ? null : Number(assigned),
        dueDate: due || null,
      });
      setTitle('');
      setDue('');
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={submit}>
      <label>
        New todo
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          placeholder="Take out the trash"
          required
        />
      </label>
      <div className="todo-add-row">
        <label>
          Assign
          <select value={assigned} onChange={(e) => setAssigned(e.target.value)}>
            <option value="">Anyone</option>
            <option value={myId}>Me</option>
            {partner && <option value={partner.id}>{partner.displayName}</option>}
          </select>
        </label>
        <label>
          Due
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        </label>
      </div>
      {error && <p className="error">{error}</p>}
      <button className="primary" disabled={busy || !title.trim()}>
        Add
      </button>
    </form>
  );
}

function TodoRow({
  todo,
  myId,
  partner,
  onChanged,
}: {
  todo: Todo;
  myId: number;
  partner: { id: number; displayName: string } | null;
  onChanged: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function toggle() {
    setBusy(true);
    try {
      await todos.update(todo.id, { status: todo.status === 'open' ? 'done' : 'open' });
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  const who = todo.assignedTo
    ? todo.assignedTo.id === myId
      ? 'Me'
      : todo.assignedTo.displayName
    : 'Anyone';

  return (
    <li className={todo.status === 'done' ? 'todo-item done' : 'todo-item'}>
      <div className="todo-main">
        <input
          type="checkbox"
          checked={todo.status === 'done'}
          disabled={busy}
          onChange={() => void toggle()}
          aria-label={`Mark ${todo.title} ${todo.status === 'open' ? 'done' : 'open'}`}
        />
        <button type="button" className="todo-body" onClick={() => setOpen((v) => !v)}>
          <strong>{todo.title}</strong>
          <span className="meta">
            {who}
            {todo.dueDate ? ` · ${shortDate(todo.dueDate)}` : ''}
            {todo.overdue ? ' · overdue' : ''}
            {todo.recurringRuleId ? ' · repeating' : ''}
            {` · ${todo.createdBy.displayName}`}
          </span>
        </button>
      </div>
      {open && (
        <TodoEditor todo={todo} myId={myId} partner={partner} onChanged={onChanged} onClose={() => setOpen(false)} />
      )}
    </li>
  );
}

function TodoEditor({
  todo,
  myId,
  partner,
  onChanged,
  onClose,
}: {
  todo: Todo;
  myId: number;
  partner: { id: number; displayName: string } | null;
  onChanged: () => Promise<void>;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(todo.title);
  const [description, setDescription] = useState(todo.description ?? '');
  const [assigned, setAssigned] = useState(todo.assignedTo ? String(todo.assignedTo.id) : '');
  const [due, setDue] = useState(todo.dueDate ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await todos.update(todo.id, {
        title: title.trim(),
        description: description.trim() || null,
        assignedToUserId: assigned === '' ? null : Number(assigned),
        dueDate: due || null,
      });
      await onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Delete “${todo.title}”?`)) return;
    setBusy(true);
    try {
      await todos.remove(todo.id);
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="todo-edit" onSubmit={save}>
      <label>
        Title
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} required />
      </label>
      <label>
        Notes
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          placeholder="Optional"
        />
      </label>
      <div className="todo-add-row">
        <label>
          Assign
          <select value={assigned} onChange={(e) => setAssigned(e.target.value)}>
            <option value="">Anyone</option>
            <option value={myId}>Me</option>
            {partner && <option value={partner.id}>{partner.displayName}</option>}
          </select>
        </label>
        <label>
          Due
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        </label>
      </div>
      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="button" className="danger" onClick={() => void remove()} disabled={busy}>
          Delete
        </button>
        <button className="primary" disabled={busy || !title.trim()}>
          Save
        </button>
      </div>
    </form>
  );
}
